import { useCurrentBranding } from '@/hooks/use-current-branding';
import { removeFromLocalStorage } from '@/lib/local-storage/local-storage';
import { getCurrentUser } from '@/requests/getCurrentUser';
import type { ErrorResponse } from '@/requests/types';
import { datadogRum } from '@datadog/browser-rum';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { SESSION_KEY } from './constants';
import { useSessionValues } from './hooks/use-session-values';
import type { CurrentUser } from './types';

export const USER_QUERY_KEY = 'current-user';

export const useGetCurrentUser = (
  options?: Omit<
    UseQueryOptions<unknown, ErrorResponse, CurrentUser, string[]>,
    'queryKey' | 'queryFn' | 'staleTime' | 'refetchOnWindowFocus'
  >
) => {
  const sessionValues = useSessionValues();
  const [branding] = useCurrentBranding();

  const user = useQuery({
    queryKey: [USER_QUERY_KEY],
    queryFn: getCurrentUser,
    // 3 minutes
    staleTime: 60 * 3 * 1000,
    refetchOnWindowFocus: true,
    retry: false,
    retryOnMount: true,
    ...options,
  });

  useEffect(() => {
    if (user.error?.status === 401) {
      removeFromLocalStorage({ key: SESSION_KEY });
    }
  }, [user.error?.status]);

  const brandingName = branding?.branding?.name;
  const userId = user.data?.id;
  const username = user.data?.username;
  const customerId = sessionValues.customerId;
  const id = `${customerId}_${user.data?.id}`;

  useEffect(() => {
    if (userId != null) {
      datadogRum.setUser({
        id,
        name: username,
        px_customer_id: customerId,
        px_user_id: userId,
      });
    }
    // Even if the type says its not undefined it still executes before we can redirect the user to login.
    if (customerId != null) {
      datadogRum.setAccount({
        id: customerId,
        name: brandingName,
      });
    }
  }, [customerId, userId, brandingName, id, username]);

  return user;
};
